import { useEffect, useMemo, useState, useCallback } from "react";
import { Settings as SettingsIcon, LogOut, Download, Filter } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import KpiRibbon from "@/components/KpiRibbon";
import EntryForm from "@/components/EntryForm";
import EntryList from "@/components/EntryList";
import FrequentInsights from "@/components/FrequentInsights";
import BulkEntry from "@/components/BulkEntry";
import PrintWeek from "@/components/PrintWeek";
import SettingsDialog from "@/components/SettingsDialog";

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday = 0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return <span className="font-mono-data text-xs text-[#9fa6b9]">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>;
}

function ViewTab({ id, label, active, onClick }) {
  const isActive = active === id;
  return (
    <button
      data-testid={`view-tab-${id}`}
      onClick={() => onClick(id)}
      className={`relative px-1 py-3.5 font-display text-base sm:text-lg font-bold uppercase tracking-tight transition-colors ${isActive ? "text-[#f3f4f8]" : "text-[#62697e] hover:text-[#9fa6b9]"}`}
    >
      {label}
      {isActive && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-amber-500" />}
    </button>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("ledger");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const settings = user?.settings || { hourly_rate: 45, multiplier: 1.5, currency: "£", projects: ["General"] };
  const currency = settings.currency || "£";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/entries");
      setEntries(data);
    } catch {
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let weekHours = 0, weekEarnings = 0, monthHours = 0, monthEarnings = 0;
    for (const e of entries) {
      const d = new Date(e.date + "T00:00:00");
      if (d >= weekStart) { weekHours += e.duration_hours; weekEarnings += e.earnings; }
      if (d >= monthStart) { monthHours += e.duration_hours; monthEarnings += e.earnings; }
    }
    return { weekHours, weekEarnings, monthHours, monthEarnings };
  }, [entries]);

  const filtered = useMemo(() => {
    if (filter === "all") return entries;
    const now = new Date();
    const from = filter === "week" ? startOfWeek(now) : new Date(now.getFullYear(), now.getMonth(), 1);
    return entries.filter((e) => new Date(e.date + "T00:00:00") >= from);
  }, [entries, filter]);

  const filteredTotals = useMemo(() => {
    return filtered.reduce((acc, e) => ({ hours: acc.hours + e.duration_hours, earnings: acc.earnings + e.earnings }), { hours: 0, earnings: 0 });
  }, [filtered]);

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        const { data } = await api.put(`/entries/${editing.id}`, payload);
        setEntries((prev) => prev.map((e) => (e.id === editing.id ? data : e)));
        setEditing(null);
        toast.success("Entry updated");
      } else {
        const { data } = await api.post("/entries", payload);
        setEntries((prev) => [data, ...prev]);
        toast.success("Overtime logged");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleBulkSubmit = async (payloads) => {
    if (!payloads.length) {
      toast.error("Select at least one day");
      return false;
    }
    try {
      const { data } = await api.post("/entries/bulk", payloads);
      setEntries((prev) => [...data, ...prev]);
      toast.success(`Added ${data.length} ${data.length === 1 ? "entry" : "entries"}`);
      setView("ledger");
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
      return false;
    }
  };

  const exportCsv = () => {
    const header = ["Date", "Project", "Start", "End", "Hours", "Rate", "Multiplier", "Earnings", "Notes"];
    const rows = filtered.map((e) => [e.date, e.project, e.start_time, e.end_time, e.duration_hours, e.hourly_rate, e.multiplier, e.earnings, (e.notes || "").replace(/"/g, '""')]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `overtime-${filter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const FilterTab = ({ value, label }) => (
    <button
      data-testid={`filter-tab-${value}`}
      onClick={() => setFilter(value)}
      className={`px-3.5 py-1.5 rounded-lg font-mono-data text-xs uppercase tracking-wide border transition-colors ${filter === value ? "bg-amber-500 text-[#0c0d10] border-amber-500" : "bg-[#14161c] text-[#9fa6b9] border-[#2b2f3d] hover:border-[#3a3f52]"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen scanlines">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0c0d10]/90 backdrop-blur-xl border-b border-[#2b2f3d] px-4 sm:px-8 py-3.5 flex items-center justify-between">
        <div data-testid="header-brand-logo" className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 live-dot" />
            <span className="font-mono-data text-[11px] sm:text-[12px] uppercase tracking-[0.22em] text-amber-500/90 font-semibold">TimeTrak</span>
          </div>
          <span className="hidden sm:inline text-[#2b2f3d]">|</span>
          <span className="hidden sm:inline"><Clock /></span>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden sm:inline rounded-full px-3 py-1 font-mono-data text-xs bg-[#20232e] text-[#f3f4f8] border border-[#2b2f3d]">
            {currency}{settings.hourly_rate}/h · {Number(settings.multiplier).toFixed(2)}x
          </span>
          <SettingsDialog
            trigger={
              <button data-testid="header-settings-button" className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] transition-colors">
                <SettingsIcon className="w-5 h-5" />
              </button>
            }
          />
          <button data-testid="header-logout-button" onClick={logout} className="p-2 rounded-lg text-[#9fa6b9] hover:text-red-400 hover:bg-[#20232e] transition-colors">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* View tabs (top of screen) */}
      <nav className="sticky top-[57px] z-40 bg-[#0c0d10]/90 backdrop-blur-xl border-b border-[#2b2f3d] px-4 sm:px-8">
        <div className="max-w-[1400px] mx-auto flex items-center gap-6">
          <ViewTab id="ledger" label="Ledger" active={view} onClick={setView} />
          <ViewTab id="frequent" label="Frequent Hours" active={view} onClick={setView} />
          <ViewTab id="bulk" label="Bulk Entry" active={view} onClick={setView} />
          <ViewTab id="print" label="Print PDF" active={view} onClick={setView} />
        </div>
      </nav>

      <main className="relative z-10 max-w-[1400px] mx-auto">
        {/* Greeting */}
        <div className="px-4 sm:px-8 pt-8">
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none text-[#f3f4f8]">
            {view === "ledger" && `${user?.name || "Your"} Ledger`}
            {view === "frequent" && "Frequent Hours"}
            {view === "bulk" && "Weekly Bulk Entry"}
            {view === "print" && "Print to PDF"}
          </h1>
          <p className="mt-2 text-[#9fa6b9]">
            {view === "ledger" && "Track every extra hour and what it's worth."}
            {view === "frequent" && "See which shifts, projects and days you work overtime most."}
            {view === "bulk" && "Log a whole week of overtime in one go."}
            {view === "print" && "Pick a start week and span to export a multi-week PDF."}
          </p>
        </div>

        {view === "ledger" && (
          <>
            {/* KPI ribbon */}
            <div className="p-4 sm:p-8 pt-6">
              <KpiRibbon stats={stats} currency={currency} />
            </div>

            {/* Workspace split */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 px-4 sm:px-8 pb-16">
              <div className="lg:col-span-4">
                <EntryForm
                  settings={settings}
                  projects={settings.projects || ["General"]}
                  onSubmit={handleSubmit}
                  editing={editing}
                  onCancelEdit={() => setEditing(null)}
                />
              </div>

              <div className="lg:col-span-8">
                {/* Filter + summary bar */}
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#62697e]" />
                    <FilterTab value="all" label="All" />
                    <FilterTab value="week" label="This Week" />
                    <FilterTab value="month" label="This Month" />
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid="export-csv-button" onClick={exportCsv} className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-mono-data text-xs uppercase tracking-wide bg-[#14161c] text-[#9fa6b9] border border-[#2b2f3d] hover:border-amber-500 hover:text-amber-500 transition-colors">
                      <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                  </div>
                </div>

                <div className="mb-4 bg-[#181a22] border border-[#2b2f3d] rounded-xl px-5 py-3.5 flex items-center justify-between">
                  <span className="font-mono-data text-[11px] uppercase tracking-[0.18em] text-[#62697e]">
                    {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
                  </span>
                  <div className="flex items-center gap-5">
                    <span data-testid="summary-total-hours" className="font-mono-data font-bold text-amber-500">{filteredTotals.hours.toFixed(2)} hrs</span>
                    <span data-testid="summary-total-earnings" className="font-mono-data font-bold text-emerald-500">
                      {currency}{filteredTotals.earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12 font-mono-data text-sm text-[#62697e]">Loading…</div>
                ) : (
                  <EntryList entries={filtered} currency={currency} onEdit={setEditing} onDelete={handleDelete} />
                )}
              </div>
            </div>
          </>
        )}

        {view === "frequent" && (
          <div className="px-4 sm:px-8 pt-6 pb-16">
            <FrequentInsights entries={entries} currency={currency} />
          </div>
        )}

        {view === "bulk" && (
          <div className="px-4 sm:px-8 pt-6 pb-16">
            <BulkEntry
              settings={settings}
              projects={settings.projects || ["General"]}
              onSubmitBulk={handleBulkSubmit}
            />
          </div>
        )}

        {view === "print" && (
          <div className="px-4 sm:px-8 pt-6 pb-16">
            <PrintWeek entries={entries} currency={currency} userName={user?.name} />
          </div>
        )}
      </main>
    </div>
  );
}
